import GlassStructure from 'components/ThreeJs/assets/filter/Glass/GlassStructure';
import { getGlassSizes } from 'domain/glass';

export default class GlassConfigUtil {
	static translate(glassConfig, lang, uiHandlerRef) {
		if (lang === glassConfig.lang) {
			return null;
		}
		if (!uiHandlerRef || !uiHandlerRef.current || !uiHandlerRef.current.isUiLoaded) {
			return null;
		}
		const translateGlass = g => (g ? uiHandlerRef.current.glassDB.translateGlass(g, lang) : '-');
		const { flatGlassStructure } = glassConfig;
		let { insulationValue } = flatGlassStructure;
		const uiHandler = uiHandlerRef.current;
		const structureUI = uiHandler.structureFilterUIElement;
		const manufacturerUI = uiHandler.manufacturerFilterUIElement;
		const fireResistanceClassUI = uiHandler.fireClassFilterUIElement;
		// const fireGlassUI = uiHandler.uiFireResistant;
		// const middleGlassUI = uiHandler.uiMiddleGlass;
		// const outerGlassUI = uiHandler.uiOuterGlass;
		const insulationUI = uiHandler.insulationFilterUIElement;
		const applicationUI = uiHandler.applicationFilterUIElement;
		const whiteGlassUI = uiHandler.whiteGlassFilterUIElement;
		const resistanceClassUI = uiHandler.resistanceClassFilterUIElement;
		const buttJointsUI = uiHandler.buttJointsFilterUIElement;
		const soundUI = uiHandler.soundFilterUIElement;
		const spacerUI = uiHandler.spacer1UIElement;
		const gasUI = uiHandler.gas1UIElement;
		if (insulationValue === '-1.0') {
			// eslint-disable-next-line no-console
			console.error('No insulation value for this glass');
			insulationValue = '5.4'; // prevent a lookup error by substituting with the worst insulation
		}
		let sound = soundUI.filter.filterOptions[flatGlassStructure.soundDampening];
		if (typeof sound === 'function') {
			const snd = sound(flatGlassStructure.soundDampening);
			sound = soundUI.t(snd.translationKey).replace('{}', snd.displayVal);
		} else {
			sound = soundUI.t(sound);
		}

		return {
			lang,
			flatGlassStructure: glassConfig.flatGlassStructure,
			thickness: `${glassConfig.flatGlassStructure.thickness} mm`,
			structure: structureUI.t(structureUI.filter.filterOptions[flatGlassStructure.structure]),
			manufacturer: manufacturerUI.t(
				manufacturerUI.filter.filterOptions[flatGlassStructure.manufacturer]
			),
			fireResistanceClass: fireResistanceClassUI.t(
				fireResistanceClassUI.filter.filterOptions[flatGlassStructure.fireResistance]
			),
			insulation:
				insulationValue === -1
					? '-'
					: insulationUI
							.t(insulationUI.filter.filterOptions[insulationValue])
							.replace('{}', insulationValue),
			application: applicationUI.t(
				applicationUI.filter.filterOptions[flatGlassStructure.insideOutsideApplication]
			),
			whiteGlass: whiteGlassUI.t(flatGlassStructure.isClearGlass ? 'yes' : 'no'),
			resistanceClass: resistanceClassUI.t(
				resistanceClassUI.filter.filterOptions[flatGlassStructure.resistanceClass]
			),
			buttJoints: buttJointsUI.t(flatGlassStructure.isButtJointCompatible ? 'yes' : 'no'),
			sound,
			fireResistant: translateGlass(
				uiHandler.glassDB.getGlassById(flatGlassStructure.fireResistantGlassId, 'frg')
			),
			spacer1: spacerUI.tVal(flatGlassStructure.spacerMiddle), // spacers and gas reversed - maybe needs (struct === '2-glazed' ? out : mid)
			spacer2: spacerUI.tVal(flatGlassStructure.spacerOutside),
			gas1: gasUI.tVal(flatGlassStructure.gasMiddle),
			gas2: gasUI.tVal(flatGlassStructure.gasOutside),
			middleGlass: flatGlassStructure.middleGlassId
				? translateGlass(uiHandler.glassDB.getGlassById(flatGlassStructure.middleGlassId, 'mid'))
				: '-',
			outsideGlass: flatGlassStructure.outerGlassId
				? translateGlass(uiHandler.glassDB.getGlassById(flatGlassStructure.outerGlassId, 'out'))
				: '-',
		};
	}

	static convertFlatGlassStructureToGlassConfig(flatGlassStructure, uiHandlerRef, lang) {
		const glassConfig = {
			lang: null,
			flatGlassStructure,
		};
		return GlassConfigUtil.translate(glassConfig, lang, uiHandlerRef);
	}

	static regenFlatGlassStructure(flatGlassStructure, db) {
		const structure = new GlassStructure();
		structure.applyFlatSnapshot(flatGlassStructure, db);
		return structure.getFlatSnapshot(db);
	}

	static importConfiguration(
		exportedConfigurationData,
		{ updatePrices = false, uiHandlerRef, currentUserAdditionalData, configuratorData }
	) {
		if (!uiHandlerRef.current?.isDbLoaded) {
			throw new Error('DB not loaded yet');
		}
		const db = uiHandlerRef.current.glassDB;
		const showPrices = currentUserAdditionalData?.company?.showPrices || false;
		/* eslint-disable no-param-reassign */
		exportedConfigurationData.glassTypes.map(gt => {
			let sqmPrice;
			let sqmPrices;
			if (!updatePrices) {
				// stash old prices before regenerating the flat glass structure which updates prices
				sqmPrice = gt.flatGlassStructure.sqmPrice;
				sqmPrices = gt.flatGlassStructure.sqmPrices
					? structuredClone(gt.flatGlassStructure.sqmPrices)
					: null;
			}
			gt.flatGlassStructure = GlassConfigUtil.regenFlatGlassStructure(gt.flatGlassStructure, db);
			if (!updatePrices) {
				gt.flatGlassStructure.sqmPrice = sqmPrice;
				if (sqmPrices === null) {
					gt.flatGlassStructure.sqmPrices = { 0: sqmPrice };
				}
			}
			return gt;
		});
		let pvc = exportedConfigurationData.totals?.totalGlassSizesSum.priceVolumeCat;
		exportedConfigurationData.totals = getGlassSizes({
			glassTypes: exportedConfigurationData.glassTypes,
			showPrice: showPrices,
			configuratorData,
			glassDb: db,
		});
		if (!updatePrices && pvc) {
			exportedConfigurationData.totals.totalGlassSizesSum.priceVolumeCat = pvc;
		}
		/* eslint-enable no-param-reassign */
		return exportedConfigurationData;
	}

	static exportConfiguration(configurationObj, { db }) {
		const out = structuredClone(configurationObj);

		delete out.userVisibility;
		delete out.adminVisibility;
		if (out.totals) delete out.totals.emptyOrZeroFields;
		// transform glassTypes
		out.glassTypes = (out.glassTypes || []).map(gt => {
			const { flatGlassStructure, positions } = gt;

			// flatten flatGlassStructure one level up
			const flattened = { ...flatGlassStructure };
			if (db) {
				flattened.verasIds = db.getVerasIds(flatGlassStructure);
			}

			// transform positions
			flattened.positions = (positions || [])
				.filter(p => !p.inactive)
				.map(p => {
					const obj = {};

					// row[] → direct fields
					p.row.forEach(item => {
						const { name, value } = item;
						obj[name === 'form' ? 'form' : name] = name === 'form' ? value === 'checked' : value;
					});
					return obj;
				});

			return flattened;
		});

		if (out.userData?.guest_order) {
			out.userData.guest_order = out.userData.guest_order === 'Ja';
		}

		return out;
	}
}
